import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, map, mergeMap, of, switchMap } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { AppState } from 'src/app/core/store/state';
import { DialogsActions } from '../../dialogs/store/actions';
import { NotificationsActions } from '../../notifications/store/actions';
import { ClausesApiService } from '../services/clauses-api.service';
import { ClauseLibraryActions } from './actions';

@Injectable()
export class FeatureEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private clausesApiService: ClausesApiService,
    private dialogRef: MatDialog,
    private translocoService: TranslocoService,
  ) {}

  getClauses$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(ClauseLibraryActions.getClauses))]).pipe(
      map(() => {
        return ClauseLibraryActions.loadClauses();
      }),
    ),
  );

  loadClauses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.loadClauses),
      switchMap(() => {
        return this.clausesApiService.getClauses$().pipe(
          map((clauses) => ClauseLibraryActions.setClauses({ clauses })),
          catchError((response) => {
            return of(ClauseLibraryActions.loadClausesFail());
          }),
        );
      }),
    ),
  );

  createClause$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.createClause),
      mergeMap((props) =>
        this.clausesApiService.createClause$(props.model).pipe(
          map((clause) => ClauseLibraryActions.createClauseSuccess({ clause })),
          catchError((response) => {
            return of(
              ClauseLibraryActions.createClauseFail(),
              DialogsActions.showDialog({
                data: {
                  actionsKeys: ['close'],
                  content: '',
                  title: this.translocoService.translate('errors.createClause'),
                },
              }),
            );
          }),
        ),
      ),
    ),
  );

  createClauseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.createClauseSuccess),
      map((props) => {
        this.store.dispatch(ClauseLibraryActions.closeCreatingClauseModal());
        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message: this.translocoService.translate('text.clauseAdded', {
              name: props.clause.name,
            }),
          }),
        );

        this.dialogRef.closeAll();
        return ClauseLibraryActions.getClauses();
      }),
    ),
  );

  createClauseCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.createClauseCancel),
      map((props) => {
        return ClauseLibraryActions.createClauseCancelled();
      }),
    ),
  );

  updateClauses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.updateClauses),
      mergeMap((props) => {
        return this.clausesApiService.batchUpdateClauses$(props.clauses).pipe(
          map(() => ClauseLibraryActions.updateClausesSuccess()),
          catchError((response) => {
            return of(ClauseLibraryActions.getClauses(), ClauseLibraryActions.updateClausesFail());
          }),
        );
      }),
    ),
  );

  updateClausesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.updateClausesSuccess),
      map(() => {
        return ClauseLibraryActions.getClauses();
      }),
    ),
  );

  deleteClause$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.deleteClause),
      map((props) => {
        return DialogsActions.showDialog({
          data: {
            actionsKeys: ['yes', 'no'],
            content: '',
            title: this.translocoService.translate('modalBody.confirmDeleteClause'),
          },
          callback: (action) => {
            if (action === 'yes') {
              return this.store.dispatch(
                ClauseLibraryActions.deleteClauseConfirmed({ uniqueId: props.uniqueId }),
              );
            } else {
              return of(
                DialogsActions.setRef({ ref: null }),
                ClauseLibraryActions.deleteClauseCancel(),
              );
            }
          },
        });
      }),
    ),
  );

  deleteClauseConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.deleteClauseConfirmed),
      mergeMap((props) => {
        return this.clausesApiService.deleteClause$(props.uniqueId).pipe(
          map(() => ClauseLibraryActions.deleteClauseSuccess()),
          catchError((response) => {
            return of(
              DialogsActions.setRef({ ref: null }),
              ClauseLibraryActions.deleteClauseFail(),
            );
          }),
        );
      }),
    ),
  );

  deleteClauseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.deleteClauseSuccess),
      map(() => {
        return ClauseLibraryActions.getClauses();
      }),
    ),
  );

  deleteClauses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.deleteClauses),
      map((props) => {
        const titleKey =
          props.uniqueIds?.length === 1
            ? 'modalBody.confirmDeleteClause'
            : 'modalBody.confirmDeleteClauses';

        return DialogsActions.showDialog({
          data: {
            actionsKeys: ['yes', 'no'],
            content: '',
            title: this.translocoService.translate(titleKey),
          },
          callback: (action) => {
            if (action === 'yes') {
              return this.store.dispatch(
                ClauseLibraryActions.deleteClausesConfirmed({ uniqueIds: props.uniqueIds }),
              );
            } else {
              return of(
                DialogsActions.setRef({ ref: null }),
                ClauseLibraryActions.deleteClausesCancel(),
              );
            }
          },
        });
      }),
    ),
  );

  deleteClausesConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.deleteClausesConfirmed),
      mergeMap((props) => {
        return this.clausesApiService.batchDeleteClauses$(props.uniqueIds).pipe(
          map(() => ClauseLibraryActions.deleteClausesSuccess()),
          catchError((response) => {
            return of(
              DialogsActions.setRef({ ref: null }),
              ClauseLibraryActions.deleteClausesFail(),
            );
          }),
        );
      }),
    ),
  );

  deleteClausesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.deleteClausesSuccess),
      map(() => {
        return ClauseLibraryActions.getClauses();
      }),
    ),
  );

  loadClause$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.loadClause),
      switchMap(({ uniqueId }) => {
        return this.clausesApiService.getClauseById$(uniqueId).pipe(
          map((clause) => ClauseLibraryActions.setClause({ clause })),
          catchError((response) => {
            return of(ClauseLibraryActions.loadClauseFail());
          }),
        );
      }),
    ),
  );

  editClauseCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.editClauseCancel),
      map((props) => {
        // if (props.pristine) {
        return ClauseLibraryActions.editClauseCancelled();
        // } else {
        //   return DialogsActions.showDialog({
        //     data: {
        //       actionsKeys: ['yes', 'no'],
        //       content: '',
        //       title: this.translocoService.translate('titles.updatesWillNotBeSaved'),
        //     },
        //     callback: (action) => {
        //       if (action === 'yes') {
        //         this.store.dispatch(ClausesActions.editClauseCancelled());
        //         this.dialogRef.closeAll();
        //         return DialogsActions.setRef({ ref: null });
        //       } else {
        //         return DialogsActions.setRef({ ref: null });
        //       }
        //     },
        //   });
        // }
      }),
    ),
  );

  editClause$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.editClause),
      mergeMap((props) =>
        this.clausesApiService.updateClause$(props.model, props.uniqueId).pipe(
          map((clause) => ClauseLibraryActions.editClauseSuccess({ clause })),
          catchError((response) => {
            return of(ClauseLibraryActions.editClauseFail());
          }),
        ),
      ),
    ),
  );

  editClauseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClauseLibraryActions.editClauseSuccess),
      map((props) => {
        this.store.dispatch(ClauseLibraryActions.closeEditingClauseModal());
        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message: this.translocoService.translate('text.clauseUpdated', {
              name: props.clause.name,
            }),
          }),
        );

        this.dialogRef.closeAll();
        return ClauseLibraryActions.getClauses();
      }),
    ),
  );
}
